import { Classes, Dialog } from "@blueprintjs/core";
import { toast } from "sonner";

import { Box, Flex } from "@/components/layout/flexbox";
import { DefaultButton } from "@/components/pieces/button/DefaultButton";
import { PrimaryButton } from "@/components/pieces/button/PrimaryButton";
import { SUPPORT_EMAIL } from "@/css/constants";
import {
  AvailableCreditsDocument,
  CoachingDashboardDocument,
  ReviewsPageDocument,
  useCancelReviewMutation,
} from "@/graphql";
import { FullReview } from "@/types/meetings";

function ClientCancelRefundableReview() {
  return (
    <Box mb={3}>
      <strong>This review is refundable. </strong>Canceling this review will
      result in a full refund.
    </Box>
  );
}

function ClientCancelNonRefundableReview() {
  return (
    <>
      <Box mb={3}>
        <strong>This review is nonrefundable. </strong>The coach has already
        started working on this review and it can no longer be canceled.
      </Box>
      <Box mb={3}>
        Please reach out to{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`} target="_BLANK">
          {SUPPORT_EMAIL}
        </a>{" "}
        for support.
      </Box>
    </>
  );
}

function CoachCancelRefundableReview() {
  return (
    <Box mb={3}>
      <strong>This review will be refunded. </strong>Cancelling this review will
      result in a full refund to the client and you will not be compensated.
    </Box>
  );
}

interface CancelReviewDialogProps {
  review: FullReview;
  isCoach: boolean;
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  customerId?: string;
}

export function CancelReviewDialog({
  review,
  isCoach,
  isOpen,
  onCancel,
  onConfirm,
  customerId,
}: CancelReviewDialogProps) {
  const [processCancel, { loading }] = useCancelReviewMutation({
    variables: {
      reviewId: review.id,
      customerId: customerId,
    },
    refetchQueries: [
      {
        query: CoachingDashboardDocument,
        variables: { customerId: review.customerId },
      },
      { query: ReviewsPageDocument },
      {
        query: AvailableCreditsDocument,
        variables: { customerId: review.customerId },
      },
    ],
    onCompleted: (data) => {
      if (data?.cancelReview?.errors?.length) {
        toast.error(data.cancelReview.errors[0]?.message);
      }
      onConfirm();
    },
    onError: () => {
      toast.error("Something went wrong, please try again soon");
    },
  });
  const refundable = !review.acceptedAt;

  return (
    <>
      <Dialog
        isOpen={isOpen}
        icon="trash"
        title={
          <Box py={2} fontSize={18}>
            Confirm cancellation?
          </Box>
        }
        canEscapeKeyClose={false}
        canOutsideClickClose={false}
        style={{ maxWidth: 550, width: "95%" }}
        onClose={onCancel}
      >
        <Box p={24}>
          <Box className={`${Classes.DIALOG_BODY} ${Classes.RUNNING_TEXT}`}>
            {isCoach ? (
              <CoachCancelRefundableReview />
            ) : refundable ? (
              <ClientCancelRefundableReview />
            ) : (
              <ClientCancelNonRefundableReview />
            )}
          </Box>
          <Flex className={Classes.DIALOG_FOOTER}>
            <Box>
              <DefaultButton disabled={loading} onClick={onCancel}>
                {!isCoach && !refundable ? "Okay" : "Nevermind"}
              </DefaultButton>
            </Box>
            {isCoach || refundable ? (
              <Box ml={3}>
                <PrimaryButton
                  intent="primary"
                  disabled={loading}
                  loading={loading}
                  onClick={() => processCancel()}
                >
                  {isCoach || refundable
                    ? "Cancel and Refund"
                    : "Cancel Without Refund"}
                </PrimaryButton>
              </Box>
            ) : null}
          </Flex>
        </Box>
      </Dialog>
    </>
  );
}
